import type { AnimationPresetDefinition } from 'adflow';
import { NAMED_EASINGS, isNamedEasing } from '@adflow/utils';

export const ANIMATION_EASINGS = Object.keys(NAMED_EASINGS);

/** Mirrors the Animate tab fields. */
export const ANIMATION_PARAMETERS = {
  duration: { type: 'number', unit: 'seconds', min: 0 },
  start: { type: 'number', unit: 'seconds', min: 0 },
  easing: { type: 'string', enum: ANIMATION_EASINGS },
  distance: { type: 'number', unit: 'pixels', min: 0, presetGroups: ['slide'] },
} as const;

type ParamKey = keyof typeof ANIMATION_PARAMETERS;

export type EffectParams = Partial<Record<ParamKey, number | string>>;

export type ParamValidation =
  | { params: EffectParams }
  | { error: string; details: { field: string; reason: string } };

function invalid(field: string, reason: string): ParamValidation {
  const name = field ? `params.${field}` : 'params';
  return { error: `${name} ${reason}`, details: { field: name, reason } };
}

export function parameterNamesFor(preset: AnimationPresetDefinition): ParamKey[] {
  return preset.group === 'slide'
    ? ['duration', 'start', 'easing', 'distance']
    : ['duration', 'start', 'easing'];
}

export function validateEffectParams(
  preset: AnimationPresetDefinition,
  raw: unknown,
): ParamValidation {
  if (raw === undefined || raw === null) {
    return { params: {} };
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return invalid('', 'must be an object.');
  }
  const allowed = parameterNamesFor(preset);
  const params: EffectParams = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!(allowed as string[]).includes(key)) {
      return invalid(key, `is not a parameter of ${preset.id}.`);
    }
    if (key === 'easing') {
      if (typeof value !== 'string' || !isNamedEasing(value)) {
        return invalid(key, `must be one of ${ANIMATION_EASINGS.join(', ')}.`);
      }
      params.easing = value;
      continue;
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return invalid(key, 'must be a finite number.');
    }
    if (value < 0) {
      return invalid(key, 'must be 0 or more.');
    }
    params[key as Exclude<ParamKey, 'easing'>] = value;
  }
  return { params };
}
